import { SlotData } from '../../types/slotData';
import { InternStatus } from '../../types/internData';
import apiClient from './apiClient';

type BatchResponseEntry = {
    code: number;
};

export const getSlotById = async (slotId: string): Promise<SlotData | null> => {
    try {
        const response = await apiClient.get('/slots', {
            params: { id: slotId }
        });
        const data = response.data.data[0];

        return {
            id: data.id,
            startDate: data.startDate,
            endDate: data.endDate
        };
    } catch (error) {
        console.error('Error getting slot:', error);
        return null;
    }
};

export const getSlotByDate = async (startAfter: string, endBefore: string, sessionId: string): Promise<Array<SlotData> | null> => {
    try {
        const response = await apiClient.get('/slots', {
            params: {
                startAfter,
                endBefore,
                sessionId
            }
        });
        const data = response.data.data;

        return data.map((slot: SlotData) => ({
            id: slot.id,
            startDate: slot.startDate,
            endDate: slot.endDate
        }));
    } catch (error) {
        console.error('Error getting slot:', error);
        return null;
    }
};

export const getSlotsBySessionId = async (sessionId: string): Promise<Array<SlotData> | null> => {
    try {
        const response = await apiClient.get('/slots', {
            params: { sessionId }
        });
        const data = response.data.data;

        return data.map((slot: SlotData) => ({
            id: slot.id,
            startDate: slot.startDate,
            endDate: slot.endDate
        }));
    } catch (error) {
        console.error('Error getting slot:', error);
        return null;
    }
};

export const getUserStatusBySlot = async (slotId: string): Promise<Array<InternStatus> | null> => {
    try {
        const response = await apiClient.get(`/slots/${slotId}/user-status`);
        const data = response.data.data;

        return data.map((user: InternStatus) => ({
            userId: user.userId,
            hasSigned: user.hasSigned,
            isPresent: user.isPresent,
            date: user.date
        }));
    } catch (error) {
        console.error('Error getting user status:', error);
        return null;
    }
};

export const createSlot = async (periodsToSend: SlotData[]): Promise<{ code: number }> => {
    try {
        const payload = periodsToSend.map(({ sessionId, startDate, endDate }) => ({
            sessionId: sessionId?.toString(),
            startDate,
            endDate
        }));

        const response = await apiClient.post('/slots/batch', payload);

        if (response.status !== 200) {
            return { code: response.status };
        }

        const codes = response.data?.data?.map((entry: BatchResponseEntry) => entry.code) || [];

        if (codes.length > 0 && codes.every((code: number) => code === 409)) {
            return { code: 409 };
        }

        return { code: 200 };
    } catch (error) {
        console.error('Error creating slot:', error);
        return { code: 500 };
    }
};

export const deleteBatchSlot = async (slotsToDelete: SlotData[]): Promise<{ code: number }> => {
    try {
        const payload = slotsToDelete.map(({ id }) => id?.toString()).filter(Boolean);

        const response = await apiClient.delete('/slots/batch', { data: payload });

        if (response.status !== 200) {
            return { code: response.status };
        }

        const codes = response.data?.data?.map((entry: BatchResponseEntry) => entry.code) || [];
        if (codes.length > 0 && codes.every((code: number) => code === 400)) {
            return { code: 400 };
        }

        return { code: 200 };
    } catch (error) {
        console.error('Error deleting slot:', error);
        return { code: 500 };
    }
};

export const deleteSlot = async (slotId: string): Promise<{ code: number }> => {
    try {
        const response = await apiClient.delete(`/slots/${slotId}`);

        if (response.status !== 200) {
            return { code: response.status };
        }

        return { code: 200 };
    } catch (error) {
        console.error('Error deleting slot:', error);
        return { code: 500 };
    }
};
